import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, users as initialUsers, getPermissionsByRole } from '../data/users';
import { authService } from '../services/auth.service';
import { RBAC_DISABLE_MOCK_ROLE_ASSIGNMENT } from '../config/rbac';

export interface Branch {
  id: string;
  restaurantId: string;
  name: string;
  code: string;
  address?: string;
  phone?: string;
  email?: string;
  isMain: boolean;
  isActive: boolean;
  manager?: string;
  openingHours?: any;
  settings?: any;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  login: (email: string, password: string) => boolean;
  loginWithUserData: (userData: any, accessToken: string) => void;
  logout: () => void;
  updateUser: (user: User) => void;
  switchActiveTenant: (tenantId: string, tenantName?: string) => void;
  /** The currently active tenant ID (overridden for super_admin users). Changes trigger a full app-tree remount via the key prop in AuthGate. */
  activeTenantId: string | null;
  mockBranches: Branch[];
}

// Default context value to prevent undefined errors during initialization
const defaultAuthContext: AuthContextType = {
  user: null,
  isAuthenticated: false,
  isInitialized: false,
  login: () => false,
  loginWithUserData: () => {},
  logout: () => {},
  updateUser: () => {},
  switchActiveTenant: () => {},
  activeTenantId: null,
  mockBranches: [],
};

const AuthContext = createContext<AuthContextType>(defaultAuthContext);

// Mock branches data
const createMockBranches = (user: User): Branch[] => [
   {
    id: 'branch-main-001',
    restaurantId: user.restaurantId || 'restaurant-001',
    name: 'Main Branch',
  code: 'MAIN',
  address: localStorage.getItem('restaurantAddress') || undefined,
    phone: '+254704930169',
    email: 'danonsombi@gmail.com',
    isMain: true,
    isActive: true,
    manager: user.name,
    openingHours: {
      sunday: { open: '10:00', close: '20:00' },
      monday: { open: '09:00', close: '21:00' },
      tuesday: { open: '09:00', close: '21:00' },
      wednesday: { open: '09:00', close: '21:00' },
      thursday: { open: '09:00', close: '21:00' },
      friday: { open: '09:00', close: '22:00' },
    },
    settings: {
      maxTables: 20,
      autoAcceptOrders: true,
    },
  },
  // {
  //   id: 'branch-main-001',
  //   restaurantId: user.restaurantId || 'restaurant-001',
  //   name: 'Main Branch',
  //   code: 'MAIN-001',
  //   address: '123 Main Street, Downtown',
  //   phone: '+1 (555) 123-4567',
  //   email: 'main@qrypt.com',
  //   isMain: true,
  //   isActive: true,
  //   manager: user.name,
  //   openingHours: {
  //     monday: { open: '09:00', close: '21:00' },
  //     tuesday: { open: '09:00', close: '21:00' },
  //     wednesday: { open: '09:00', close: '21:00' },
  //     thursday: { open: '09:00', close: '21:00' },
  //     friday: { open: '09:00', close: '22:00' },
  //     saturday: { open: '10:00', close: '22:00' },
  //     sunday: { open: '10:00', close: '20:00' },
  //   },
  //   settings: {
  //     maxTables: 20,
  //     autoAcceptOrders: true,
  //   },
  // },
  // {
  //   id: 'branch-002',
  //   restaurantId: user.restaurantId || 'restaurant-001',
  //   name: 'Downtown Branch',
  //   code: 'DT-002',
  //   address: '456 Downtown Ave',
  //   phone: '+1 (555) 234-5678',
  //   email: 'downtown@qrypt.com',
  //   isMain: false,
  //   isActive: true,
  //   manager: 'John Manager',
  // },
  // {
  //   id: 'branch-003',
  //   restaurantId: user.restaurantId || 'restaurant-001',
  //   name: 'Airport Branch',
  //   code: 'AP-003',
  //   address: '789 Airport Rd',
  //   phone: '+1 (555) 345-6789',
  //   email: 'airport@qrypt.com',
  //   isMain: false,
  //   isActive: true,
  //   manager: 'Sarah Branch Manager',
  // },
];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [mockBranches, setMockBranches] = useState<Branch[]>([]);
  // Tracked as React state so AuthGate can key AppContent on it, causing a clean remount on tenant switch.
  const [activeTenantId, setActiveTenantId] = useState<string | null>(() => localStorage.getItem('activeTenantId'));
  const [isInitialized, setIsInitialized] = useState(false);

  const normalizeRole = (role?: string): User['role'] => {
    const normalized = (role || 'viewer').toLowerCase();
    if (normalized === 'super_admin' || normalized === 'super-admin') return 'super_admin';
    if (normalized === 'admin') return 'admin';
    if (normalized === 'manager') return 'manager';
    if (normalized === 'chef') return 'chef';
    if (normalized === 'staff') return 'staff';
    return 'viewer';
  };

  const resolvePermissions = (role: User['role'], sourcePermissions: unknown): string[] => {
    const fromApi = Array.isArray(sourcePermissions) ? sourcePermissions.filter((p) => typeof p === 'string') : [];
    // Always trust API-provided permissions when present — they come fresh from the DB.
    // Only fall back to hardcoded role map when the API returned nothing at all
    // (e.g. very old token format) AND RBAC_DISABLE_MOCK_ROLE_ASSIGNMENT is not set.
    if (fromApi.length > 0) {
      return fromApi;
    }
    if (RBAC_DISABLE_MOCK_ROLE_ASSIGNMENT) {
      return [];
    }
    return getPermissionsByRole(role);
  };

  const clearTenantScopedCache = () => {
    localStorage.removeItem('branches');
    localStorage.removeItem('currentBranch');
    localStorage.removeItem('selectedTable');
    localStorage.removeItem('restaurantName');
    localStorage.removeItem('restaurantAddress');
    localStorage.removeItem('restaurantLogo');
    localStorage.removeItem('brandColors');
    localStorage.removeItem('orderByTable');
    localStorage.removeItem('theme');
  };

  const applyActiveTenantOverride = (candidateUser: User): User => {
    const canSwitchTenants =
      (candidateUser.role || '').toLowerCase() === 'super_admin' ||
      candidateUser.permissions?.includes('tenants_platform');
    const activeTenantId = canSwitchTenants ? localStorage.getItem('activeTenantId') : null;

    if (!activeTenantId) {
      return candidateUser;
    }

    return {
      ...candidateUser,
      tenantId: activeTenantId,
      restaurantId: activeTenantId,
    };
  };

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('accessToken');

        if (token) {
          // Hydrate user + effective tenant + active branch context in one backend call.
          const response = await authService.getSessionContext();

          if (response.success && response.data) {
            // Map backend session user to frontend User type
            const userData: any = response.data.user || response.data;
            const mappedRole = normalizeRole(userData.role);
            const mappedUser: User = {
              id: userData.id,
              name: userData.name || `${(userData as any).firstName || ''} ${(userData as any).lastName || ''}`.trim() || 'User',
              email: userData.email,
              password: '',
              role: mappedRole,
              avatar: userData.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
              phone: (userData as any).phone || undefined,
              bio: (userData as any).bio || undefined,
              location: (userData as any).location || undefined,
              status: 'active',
              createdAt: userData.createdAt || new Date().toISOString(),
              lastLogin: new Date().toISOString(),
              permissions: resolvePermissions(mappedRole, userData.permissions),
              restaurantId: (userData as any).tenantId,
              tenantId: (userData as any).tenantId,
              branchId: (userData as any).branchId,
              branchName: (userData as any).branch?.name || 'Main Branch',
            };

            // Keep active tenant state and cache aligned with backend effective context.
            if ((userData as any).tenantId) {
              localStorage.setItem('activeTenantId', (userData as any).tenantId);
              setActiveTenantId((userData as any).tenantId);
            }

            const activeBranch = (response.data as any).activeBranch || (userData as any).branch || null;
            if (activeBranch) {
              const branchForStore = {
                id: activeBranch.id,
                restaurantId: activeBranch.tenantId || (userData as any).tenantId,
                name: activeBranch.name,
                code: activeBranch.code,
                address: activeBranch.address || undefined,
                phone: activeBranch.phone || undefined,
                email: activeBranch.email || undefined,
                isMain: Boolean(activeBranch.isMain),
                isActive: activeBranch.isActive !== false,
              };
              localStorage.setItem('currentBranch', JSON.stringify(branchForStore));
              const tenantId = (userData as any).tenantId;
              if (tenantId) {
                try {
                  const mapRaw = localStorage.getItem('currentBranchByTenant');
                  const map = mapRaw ? JSON.parse(mapRaw) || {} : {};
                  map[tenantId] = branchForStore;
                  localStorage.setItem('currentBranchByTenant', JSON.stringify(map));
                } catch {
                  // Ignore malformed cache map.
                }
              }
            }

            setUser(mappedUser);
            setMockBranches(createMockBranches(mappedUser));
            localStorage.setItem('user', JSON.stringify(mappedUser));
          } else {
            console.warn('🔐 AuthContext - API returned invalid data, using localStorage fallback.');

            // Try to get user from localStorage as fallback but STRIP permissions
            // so stale cached permissions never silently override fresh API data.
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
              try {
                const parsedUser = JSON.parse(storedUser);
                // Clear permissions — they will be re-fetched on next successful API call.
                // This prevents stale over-privileged permissions surviving across deploys.
                parsedUser.permissions = [];
                const restoredUser = applyActiveTenantOverride(parsedUser);
                setUser(restoredUser);
                setMockBranches(createMockBranches(restoredUser));
              } catch (e) {
                console.error('🔐 AuthContext - Failed to parse stored user:', e);
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('user');
              }
            } else {
              localStorage.removeItem('accessToken');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user');
            }
          }
        }
      } catch (error) {
        console.error('🔐 AuthContext - Failed to initialize auth:', error);

        // Try localStorage fallback on error but STRIP permissions
        // so stale cached permissions never silently override fresh API data.
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            // Clear permissions — they will be re-fetched on next successful API call.
            parsedUser.permissions = [];
            const restoredUser = applyActiveTenantOverride(parsedUser);
            setUser(restoredUser);
            setMockBranches(createMockBranches(restoredUser));
          } catch (e) {
            console.error('🔐 AuthContext - Failed to parse stored user:', e);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
          }
        } else {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        }
      } finally {
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  const login = (email: string, password: string): boolean => {
    if (RBAC_DISABLE_MOCK_ROLE_ASSIGNMENT) {
      return false;
    }

    const foundUser = initialUsers.find(
      u => u.email === email && u.password === password && u.status === 'active'
    );

    if (foundUser) {
      const nextUser = applyActiveTenantOverride(foundUser);
      setUser(nextUser);
      // Create mock branches for the user
      setMockBranches(createMockBranches(nextUser));
      return true;
    }
    return false;
  };

  const loginWithUserData = (userData: any, accessToken: string) => {
    // Map backend user data to frontend User type
    // This function handles login with data from the backend API
    const mappedRole = normalizeRole(userData.role);
    const mappedUser: User = applyActiveTenantOverride({
      id: userData.id,
      name: userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'User',
      email: userData.email,
      password: '', // Don't store password in frontend
      role: mappedRole,
      avatar: userData.profilePicture || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
      phone: userData.phone || undefined,
      bio: userData.bio || undefined,
      location: userData.location || undefined,
      status: 'active',
      createdAt: userData.createdAt || new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      permissions: resolvePermissions(mappedRole, userData.permissions),
      restaurantId: userData.tenantId,
      tenantId: userData.tenantId, // Also set tenantId for compatibility
      branchId: userData.branchId,
      branchName: userData.branch?.name || 'Main Branch',
    });

    setUser(mappedUser);
    // Create mock branches for the user
    setMockBranches(createMockBranches(mappedUser));
    
    // Store token in localStorage (authService already does this, but ensure it's stored)
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('user', JSON.stringify(mappedUser));
  };

  const logout = async () => {
    try {
      // Call backend logout endpoint
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless of API call result
      setUser(null);
      setMockBranches([]);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('activeTenantId');
      setActiveTenantId(null);
      localStorage.removeItem('activeTenantName');
      clearTenantScopedCache();
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const switchActiveTenant = (tenantId: string, tenantName?: string) => {
    const canSwitchTenants =
      !!user &&
      (((user.role || '').toLowerCase() === 'super_admin') || user.permissions?.includes('tenants_platform'));

    if (!canSwitchTenants) {
      return;
    }

    const nextUser: User = {
      ...user,
      tenantId,
      restaurantId: tenantId,
      branchId: undefined,
      branchName: 'Main Branch',
    };

    if (tenantName) {
      localStorage.setItem('activeTenantName', tenantName);
    }
    localStorage.setItem('activeTenantId', tenantId);

    // Clear ALL per-tenant branch cache so RestaurantContext resolves the new tenant's main branch.
    localStorage.removeItem('currentBranchByTenant');

    clearTenantScopedCache();
    setUser(nextUser);
    setMockBranches(createMockBranches(nextUser));
    localStorage.setItem('user', JSON.stringify(nextUser));
    // Update state last — this causes AuthGate to provide a new key to AppContent, remounting the entire
    // app tree cleanly for the new tenant. All hooks reinitialize without any window.location.reload().
    setActiveTenantId(tenantId);
  };

  // Always provide the context, even during initialization
  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated: !!user, 
      isInitialized,
      login, 
      loginWithUserData,
      logout,
      updateUser,
      switchActiveTenant,
      activeTenantId,
      mockBranches
    }}>
      {!isInitialized ? (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading...</p>
          </div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  // Context now has default value, so it will never be undefined
  return context;
}